import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ProductModalProps {
  product: {
    id: number;
    name: string;
    category: string;
    description: string;
    image: string;
    details: {
      origin: string;
      packaging: string;
      shelfLife: string;
      certifications: string[];
    };
    specifications: Array<{ label: string; value: string }>;
  };
  onClose: () => void;
}

export default function ProductModal({ product, onClose }: ProductModalProps) {
  const navigate = useNavigate();
  
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const allCertifications = [
    'FSSAI',
    'APEDA',
    'Spice Board',
    'Certificate of Origin (COO)',
    'Halal Certificate',
    'Port Authority Multiple Ports',
    'MSME',
    'GST'
  ];

  const handleRequestQuotation = () => {
    onClose();
    navigate('/contact');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl sm:rounded-3xl max-w-5xl w-full my-8 relative animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors cursor-pointer shadow-lg z-10"
        >
          <i className="ri-close-line text-2xl text-gray-700"></i>
        </button>

        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-10 p-6 sm:p-8 lg:p-10">
          {/* Left - Image */}
          <div className="space-y-4">
            <div className="rounded-2xl overflow-hidden bg-gray-100 w-full aspect-[4/3]">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                {product.category}
              </span>
              <span className="px-3 py-1.5 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                Export Quality
              </span>
            </div>
          </div>

          {/* Right - Details */}
          <div className="flex flex-col">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">{product.name}</h2>
            <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-6">
              {product.description}
            </p>

            {/* Basic Details */}
            <div className="mb-6 p-4 sm:p-5 bg-gray-50 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Packaging</span>
                <span className="text-sm font-medium text-gray-900 text-right">{product.details.packaging}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Shelf Life</span>
                <span className="text-sm font-medium text-gray-900">{product.details.shelfLife}</span>
              </div>
            </div>

            {/* Specifications */}
            <div className="mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Specifications</h3>
              <div className="space-y-2">
                {product.specifications.map((spec, index) => (
                  <div key={index} className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
                    <span className="text-sm text-gray-600">{spec.label}</span>
                    <span className="text-sm font-medium text-gray-900 text-right">{spec.value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications */}
            <div className="mb-6">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Certifications & Compliance</h3>
              <div className="flex flex-wrap gap-2">
                {allCertifications.map((cert, index) => (
                  <span
                    key={index}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs font-medium rounded-full"
                  >
                    {cert}
                  </span>
                ))}
              </div>
            </div>

            {/* CTA */}
            <button 
              onClick={handleRequestQuotation}
              className="w-full px-6 py-3.5 sm:py-4 bg-emerald-700 text-white rounded-xl text-sm sm:text-base font-semibold hover:bg-emerald-800 transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl mt-auto"
            >
              Request Quotation
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
